import type { VoiceTypeKey } from '@/src/dal/user-dal'
import type { RefObject } from 'react'
import { useEffect } from 'react'
import { Modal, Platform, Pressable, Text, TextInput, View } from 'react-native'
import { ExternalLinkIcon } from '@/src/components/ui/ExternalLinkIcon'
import { WarningIcon } from '@/src/components/ui/WarningIcon'
import { useTranslation } from 'react-i18next'

import { ErrorIcon } from '../../../components/ui/ErrorIcon'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { Spinner } from '../../../components/ui/Spinner'
import { colors } from '../../../theme'
import {
  body2,
  body3,
  body4,
  headline3,
  label1,
  label2,
} from '../../../theme/typography'

const removeAccountActionRowSubtitleStyle = {
  color: colors.textBody,
  fontSize: 13,
  fontWeight: '400',
  letterSpacing: 0,
  lineHeight: 18,
  marginTop: -3,
} as const

const removeAccountSmallButtonTextStyle = {
  lineHeight: 26,
  letterSpacing: 0,
} as const

const removeAccountBlockedDescriptionStyle = {
  color: colors.textBody,
  fontSize: 14,
  fontWeight: '400',
  letterSpacing: 0,
  lineHeight: 19,
  marginBottom: 18,
} as const

const NOOP = () => {}

type LanguageSelectionModalProps = {
  visible: boolean
  language: string
  onClose: () => void
  onSelectLanguage: (language: 'en' | 'sv') => void
}

export function LanguageSelectionModal({
  visible,
  language,
  onClose,
  onSelectLanguage,
}: LanguageSelectionModalProps) {
  const { t } = useTranslation(['profile', 'common'])

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable className="flex-1" onPress={onClose}>
        <View className="flex-1 justify-center items-center bg-black/50">
          <Pressable onPress={NOOP}>
            <View className="bg-background rounded-lg w-[320px] overflow-hidden">
              <View className="px-5 pt-5 pb-4">
                <Text style={[headline3, { textAlign: 'center' }]}>
                  {t('profile:change_language')}
                </Text>
              </View>
              <Pressable
                className="px-5 py-3.5 flex-row items-center justify-between border-t border-border/50 active:bg-fill"
                onPress={() => onSelectLanguage('en')}
              >
                <Text style={[label2, { color: colors.textHeader }]}>
                  {t('profile:language_en')}
                </Text>
                {language.startsWith('en') && (
                  <Icon
                    name={symbolName('checkmark', 'check')}
                    size={20}
                    color={colors.primary}
                  />
                )}
              </Pressable>
              <Pressable
                className="px-5 py-3.5 flex-row items-center justify-between border-t border-border/50 active:bg-fill"
                onPress={() => onSelectLanguage('sv')}
              >
                <Text style={[label2, { color: colors.textHeader }]}>
                  {t('profile:language_sv')}
                </Text>
                {language.startsWith('sv') && (
                  <Icon
                    name={symbolName('checkmark', 'check')}
                    size={20}
                    color={colors.primary}
                  />
                )}
              </Pressable>
              <Pressable
                className="px-5 py-3.5 items-center border-t border-border/50 active:bg-fill"
                onPress={onClose}
              >
                <Text style={[label2, { color: colors.destructive }]}>
                  {t('common:cancel')}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  )
}

type VoiceTypeSelectionModalProps = {
  visible: boolean
  selectedVoiceType: VoiceTypeKey | null
  voiceTypeOptions: { key: VoiceTypeKey; label: string }[]
  onClose: () => void
  onSelectVoiceType: (voiceType: VoiceTypeKey) => void
}

export function VoiceTypeSelectionModal({
  visible,
  selectedVoiceType,
  voiceTypeOptions,
  onClose,
  onSelectVoiceType,
}: VoiceTypeSelectionModalProps) {
  const { t } = useTranslation(['onboarding', 'common'])

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable className="flex-1" onPress={onClose}>
        <View className="flex-1 justify-center items-center bg-black/50 px-5">
          <Pressable onPress={NOOP}>
            <View
              className="bg-background rounded-lg overflow-hidden w-full"
              style={{ maxWidth: 400 }}
            >
              <View className="px-5 pt-5 pb-4">
                <Text style={[headline3, { textAlign: 'center' }]}>
                  {t('onboarding:post_payment.voice_type')}
                </Text>
              </View>
              {voiceTypeOptions.map((voiceType) => (
                <Pressable
                  key={voiceType.key}
                  className="px-5 py-3.5 flex-row items-center justify-between border-t border-border/50 active:bg-fill"
                  onPress={() => onSelectVoiceType(voiceType.key)}
                >
                  <Text style={[label2, { color: colors.textHeader }]}>
                    {voiceType.label}
                  </Text>
                  {selectedVoiceType === voiceType.key ? (
                    <Icon
                      name={symbolName('checkmark', 'check')}
                      size={20}
                      color={colors.primary}
                    />
                  ) : null}
                </Pressable>
              ))}
              <Pressable
                className="px-5 py-3.5 items-center border-t border-border/50 active:bg-fill"
                onPress={onClose}
              >
                <Text style={[label2, { color: colors.destructive }]}>
                  {t('common:cancel')}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  )
}

type EditNameModalProps = {
  visible: boolean
  inputRef: RefObject<TextInput | null>
  value: string
  error: string | null
  canSave: boolean
  isSaving: boolean
  onChangeText: (text: string) => void
  onClose: () => void
  onSave: () => void
}

export function EditNameModal({
  visible,
  inputRef,
  value,
  error,
  canSave,
  isSaving,
  onChangeText,
  onClose,
  onSave,
}: EditNameModalProps) {
  const { t } = useTranslation(['profile', 'common'])

  useEffect(() => {
    if (!visible) return

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [inputRef, visible])

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable className="flex-1" onPress={onClose}>
        <View className="flex-1 justify-center items-center bg-black/50 px-5">
          <Pressable onPress={NOOP}>
            <View
              className="bg-background rounded-lg overflow-hidden w-full"
              style={{ maxWidth: 400 }}
            >
              <View className="px-6 pt-6 pb-2">
                <Text
                  style={[
                    headline3,
                    { color: colors.textHeader, textAlign: 'center' },
                  ]}
                >
                  {t('profile:edit_name')}
                </Text>
                <Text
                  style={[
                    body3,
                    {
                      color: colors.muted,
                      textAlign: 'center',
                      marginTop: 4,
                    },
                  ]}
                >
                  {t('profile:edit_name_description')}
                </Text>
              </View>
              <View className="px-6 pt-4 pb-6">
                <TextInput
                  ref={inputRef}
                  className="h-12 bg-background border border-border rounded-lg px-4 py-0 text-base"
                  style={{
                    color: colors.textHeader,
                    lineHeight: 20,
                    includeFontPadding: false,
                  }}
                  placeholder={t('profile:edit_name_placeholder')}
                  placeholderTextColor={colors.muted}
                  value={value}
                  onChangeText={onChangeText}
                  autoCapitalize="words"
                  autoCorrect={false}
                  returnKeyType="done"
                  textAlignVertical="center"
                  onSubmitEditing={() => {
                    if (canSave) onSave()
                  }}
                />
                {error != null && (
                  <Text
                    style={[body4, { color: colors.destructive, marginTop: 6 }]}
                  >
                    {error}
                  </Text>
                )}
                <Pressable
                  className="rounded-lg py-3 items-center mt-4 active:opacity-80"
                  style={{
                    backgroundColor: canSave ? colors.primary : colors.grayMid,
                  }}
                  onPress={onSave}
                  disabled={!canSave}
                >
                  {isSaving ? (
                    <Spinner color={colors.primaryForeground} />
                  ) : (
                    <Text style={[label1, { color: colors.primaryForeground }]}>
                      {t('common:save')}
                    </Text>
                  )}
                </Pressable>
                <Pressable
                  className="rounded-lg py-3 items-center bg-fill active:opacity-70 mt-2"
                  onPress={onClose}
                >
                  <Text style={[label2, { color: colors.textBody }]}>
                    {t('common:cancel')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  )
}

type RemoveAccountDialogProps = {
  visible: boolean
  step: 'unsubscribe' | 'confirm'
  eligible: boolean | undefined
  isDeleting: boolean
  isCheckingEligibility: boolean
  onClose: () => void
  onContinueAfterUnsubscribe: () => void
  onDeleteAccount: () => void
  onOpenUnsubscribeLink: () => void
}

export function RemoveAccountDialog({
  visible,
  step,
  eligible,
  isDeleting,
  isCheckingEligibility,
  onClose,
  onContinueAfterUnsubscribe,
  onDeleteAccount,
  onOpenUnsubscribeLink,
}: RemoveAccountDialogProps) {
  const { t } = useTranslation(['profile', 'common'])
  const isUnsubscribeStep = step === 'unsubscribe'
  const title = isUnsubscribeStep
    ? t('profile:remove_account_blocked_title')
    : t('profile:remove_account_confirm_title')
  const message = isUnsubscribeStep
    ? t('profile:remove_account_blocked_message')
    : t('profile:remove_account_confirm_message')

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable className="flex-1" onPress={onClose}>
        <View className="flex-1 justify-center items-center bg-black/50 px-5">
          <Pressable onPress={NOOP}>
            <View
              className="bg-background overflow-hidden w-full rounded-lg"
              style={{ maxWidth: 400 }}
            >
              <View
                className={`items-center ${isUnsubscribeStep ? 'pt-5 pb-2' : 'px-5 pt-5 pb-0'}`}
              >
                <View
                  className="mb-2 h-16 w-16 items-center justify-center rounded-full"
                  style={{ backgroundColor: 'rgba(221, 24, 24, 0.14)' }}
                >
                  {isUnsubscribeStep ? <ErrorIcon /> : <WarningIcon />}
                </View>
                <Text
                  style={[
                    label1,
                    {
                      color: colors.textHeader,
                      textAlign: 'center',
                      fontSize: 16,
                      lineHeight: 30,
                      letterSpacing: 0,
                    },
                  ]}
                >
                  {title}
                </Text>
              </View>

              <View
                className={`px-4 ${isUnsubscribeStep ? 'pt-1 pb-4' : 'pt-2 pb-8'}`}
              >
                <Text
                  style={[
                    isUnsubscribeStep ? body3 : body2,
                    isUnsubscribeStep
                      ? removeAccountBlockedDescriptionStyle
                      : {
                          color: colors.textBody,
                          marginBottom: 18,
                          fontSize: 14,
                          lineHeight: 22,
                          letterSpacing: 0,
                        },
                  ]}
                >
                  {message}
                </Text>

                {isUnsubscribeStep && (
                  <View className="mb-4">
                    <Pressable
                      className="mb-3 rounded-lg bg-secondary700 px-4 active:opacity-70"
                      style={{ height: 66 }}
                      onPress={onOpenUnsubscribeLink}
                    >
                      <View className="h-full flex-row items-center">
                        <View className="flex-1 justify-center">
                          <Text
                            style={[
                              label1,
                              {
                                color: colors.textHeader,
                                lineHeight: 30,
                                letterSpacing: 0,
                              },
                            ]}
                          >
                            {Platform.OS === 'android'
                              ? t('profile:remove_account_unsubscribe_android')
                              : Platform.OS === 'web'
                                ? t('profile:remove_account_unsubscribe_web')
                                : t('profile:remove_account_unsubscribe_ios')}
                          </Text>
                          <Text
                            style={[body3, removeAccountActionRowSubtitleStyle]}
                          >
                            {Platform.OS === 'android'
                              ? t(
                                  'profile:remove_account_unsubscribe_hint_android',
                                )
                              : Platform.OS === 'web'
                                ? t(
                                    'profile:remove_account_unsubscribe_hint_web',
                                  )
                                : t(
                                    'profile:remove_account_unsubscribe_hint_ios',
                                  )}
                          </Text>
                        </View>
                        <ExternalLinkIcon color={colors.inkSecondary} />
                      </View>
                    </Pressable>

                    {eligible ? (
                      <Pressable
                        className="h-10.5 rounded-lg items-center justify-center active:opacity-80"
                        style={{
                          backgroundColor: isDeleting
                            ? colors.grayMid
                            : colors.destructive,
                        }}
                        onPress={onDeleteAccount}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Spinner color={colors.primaryForeground} />
                        ) : (
                          <Text
                            style={[
                              label2,
                              { color: colors.primaryForeground },
                              removeAccountSmallButtonTextStyle,
                            ]}
                          >
                            {t('profile:remove_account_final_confirm')}
                          </Text>
                        )}
                      </Pressable>
                    ) : (
                      <Pressable
                        className="h-10.5 rounded-lg items-center justify-center active:opacity-80"
                        style={{
                          backgroundColor: isCheckingEligibility
                            ? colors.grayMid
                            : colors.primary,
                        }}
                        onPress={onContinueAfterUnsubscribe}
                        disabled={isCheckingEligibility}
                      >
                        {isCheckingEligibility ? (
                          <Spinner color={colors.primaryForeground} />
                        ) : (
                          <Text
                            style={[
                              label2,
                              { color: colors.primaryForeground },
                              removeAccountSmallButtonTextStyle,
                            ]}
                          >
                            {t('profile:remove_account_refresh_status')}
                          </Text>
                        )}
                      </Pressable>
                    )}
                  </View>
                )}

                {!isUnsubscribeStep && (
                  <View className="mb-3">
                    <Pressable
                      className="h-10.5 rounded-lg items-center justify-center active:opacity-80"
                      style={{
                        backgroundColor: isDeleting
                          ? colors.grayMid
                          : colors.destructive,
                      }}
                      onPress={onDeleteAccount}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Spinner color={colors.primaryForeground} />
                      ) : (
                        <Text
                          style={[
                            label2,
                            { color: colors.primaryForeground },
                            removeAccountSmallButtonTextStyle,
                          ]}
                        >
                          {t('profile:remove_account_final_confirm')}
                        </Text>
                      )}
                    </Pressable>
                  </View>
                )}

                <Pressable
                  className="h-10.5 rounded-lg bg-background items-center justify-center active:opacity-70"
                  style={{
                    borderWidth: 1,
                    borderColor: colors.textHeader,
                  }}
                  onPress={onClose}
                >
                  <Text
                    style={[
                      label2,
                      { color: colors.textHeader },
                      removeAccountSmallButtonTextStyle,
                    ]}
                  >
                    {t('common:cancel')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  )
}
